import { HelpCircle, AlertTriangle, ExternalLink, FileSpreadsheet, X } from 'lucide-react'
import { useScopeStore } from '../store'
import { getFieldById, getGroupById, getSectionById } from '../schema'
import { Tooltip } from '../../../components/ui/Tooltip'
import { useHasResellerParent, ResellerInheritedBanner } from './ResellerLock'
import { useUpsellContext } from '../hooks/useUpsellContext'

export function RulesTab() {
  const { scope, updateRulesConfig, updateRiskConfig } = useScopeStore()
  const { rulesConfig, riskConfig } = scope
  const resellerImported = useHasResellerParent()
  const disabled = scope.finalized || resellerImported

  const tabHeaderGroup = getGroupById('rules_tab_header')
  const fraudSection = getSectionById('rules_fraud_monitoring')
  const fraudGroup = getGroupById('rules_fraud_group')
  const fraudEnabledField = getFieldById('rules_fraud_enabled')
  const fraudTypesField = getFieldById('rules_fraud_types')
  const fraudRequirementsField = getFieldById('rules_fraud_requirements')

  const riskSection = getSectionById('rules_risk_configuration')
  const riskGroup = getGroupById('rules_risk_configuration_group')

  // Style for incomplete required fields — suppressed for upsells
  const { incompleteClass, isUpsell, mapLabel } = useUpsellContext()

  const fraudTypeTooltips: Record<string, string> = {
    device_intelligence:
      'Creates a unique fingerprint for the user\'s device by collecting hundreds of data points (device model, OS, browser, IP, location). Detects VPN/proxy usage, emulators, devices previously linked to fraud, and mismatches like logins from new devices in different countries — strong indicators of account takeover.',
    behavioural_biometrics:
      'Analyzes how a user interacts with their device — typing speed, mouse movements, phone hold angle, tap pressure, and scroll patterns. Distinguishes between humans and bots, and between the real user and an imposter who may hesitate or navigate differently after stealing credentials.',
    phone_risk:
      'Assesses risk associated with a phone number by examining its history and carrier data. Identifies disposable VoIP numbers (high risk) vs. long-established carrier numbers (low risk). Detects recent SIM swaps — a major red flag for account takeover where fraudsters port the victim\'s number to their own device.',
    email_risk:
      'Analyzes the reputation and history of an email address. A newly created email from a suspicious domain is far riskier than a well-established address linked to social media accounts. Checks the email\'s age, domain reputation, and whether it has appeared in known data breaches to score risk.',
    transaction_monitoring:
      'Continuous, real-time analysis of user activity after sign-up or login — financial transactions (payments, transfers) and non-financial activity (logins, password changes). Establishes a baseline of normal behavior and flags anomalies like sudden large transfers to new international accounts.',
  }

  const isFraudIncomplete = !isUpsell && rulesConfig.fraudEnabled === 'unknown'

  return (
    <div className="space-y-6">
      <ResellerInheritedBanner />
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <h3 className="text-lg font-medium text-[var(--text-primary)]">
          {tabHeaderGroup?.formTitle || 'Risk & Fraud'}
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {tabHeaderGroup?.formDescription ||
            'Configure fraud monitoring and risk configuration'}
        </p>
      </div>

      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {fraudSection?.formTitle || fraudGroup?.formTitle || 'Fraud & Transaction Monitoring'}
        </h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <div className={isFraudIncomplete ? incompleteClass : ''}>
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              {fraudEnabledField?.formLabel || 'Fraud enabled'}
            </label>
            {fraudEnabledField?.tooltip && (
              <Tooltip content={fraudEnabledField.tooltip}>
                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
              </Tooltip>
            )}
          </div>
          <select
            className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
            value={rulesConfig.fraudEnabled}
            onChange={(e) => {
              updateRulesConfig('fraudEnabled', e.target.value as any)
            }}
            disabled={disabled}
          >
            {(
              (fraudEnabledField?.options || [
                { value: 'unknown', label: mapLabel("Don't know") },
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]) as Array<{ value: string; label: string }>
            ).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {mapLabel(opt.label)}
              </option>
            ))}
          </select>
        </div>

        {rulesConfig.fraudEnabled === 'yes' && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                {fraudTypesField?.formLabel || 'Fraud Types'}
              </label>
              {fraudTypesField?.tooltip && (
                <Tooltip content={fraudTypesField.tooltip}>
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(
                (fraudTypesField?.options || [
                  { value: 'device_intelligence', label: 'Device Intelligence' },
                  { value: 'behavioural_biometrics', label: 'Behavioural Biometrics' },
                  { value: 'phone_risk', label: 'Phone Risk Assessment' },
                  { value: 'email_risk', label: 'Email Risk Assessment' },
                  { value: 'transaction_monitoring', label: 'Transaction & Activity Monitoring' },
                ]) as Array<{ value: string; label: string }>
              ).map((opt) => {
                const checked = (rulesConfig.fraudTypes || []).includes(opt.value)
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      checked
                        ? 'bg-primary/10 border-primary text-[var(--text-primary)]'
                        : 'bg-[var(--bg-secondary)] border-[var(--border-color)] text-[var(--text-secondary)]'
                    } ${disabled ? (checked ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : 'hover:border-primary/50'}`}
                  >
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={checked}
                      onChange={() => {
                        if (disabled) return
                        const current = rulesConfig.fraudTypes || []
                        const updated = checked
                          ? current.filter((v) => v !== opt.value)
                          : [...current, opt.value]
                        updateRulesConfig('fraudTypes', updated)
                      }}
                      disabled={disabled}
                    />
                    <span className="text-sm flex-1">{opt.label}</span>
                    {fraudTypeTooltips[opt.value] && (
                      <Tooltip content={fraudTypeTooltips[opt.value]}>
                        <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] shrink-0" />
                      </Tooltip>
                    )}
                  </label>
                )
              })}
            </div>

            {(rulesConfig.fraudTypes || []).includes('transaction_monitoring') && (
              <div className="mt-3 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
                <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-[var(--text-secondary)] space-y-1">
                  <p className="font-medium text-amber-400">Client integration required for Transaction & Activity Monitoring</p>
                  <p>The client must instrument their app to send activities/transactions to the monitoring API in real time (e.g. POST /v2/activities at deposit, withdrawal, transfer). Optionally, the OneSDK frontend can capture richer device and behavioural signals.</p>
                  <p>Asynchronous alerts and rule results are delivered via webhooks (Alert Origination, Alert Resolution, Workflow Complete). Analysts review and resolve alerts in the vendor portal or via API-driven automation.</p>
                </div>
              </div>
            )}

            {((rulesConfig.fraudTypes || []).includes('device_intelligence') ||
              (rulesConfig.fraudTypes || []).includes('behavioural_biometrics')) && (
              <div className="mt-3 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
                <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-[var(--text-secondary)] space-y-1">
                  <p className="font-medium text-amber-400">
                    OneSDK frontend integration required for{' '}
                    {[
                      (rulesConfig.fraudTypes || []).includes('device_intelligence') && 'Device Intelligence',
                      (rulesConfig.fraudTypes || []).includes('behavioural_biometrics') && 'Behavioural Biometrics',
                    ]
                      .filter(Boolean)
                      .join(' & ')}
                  </p>
                  <p>The client must embed OneSDK in their web or mobile frontend. OneSDK (with embedded Sardine) silently collects device telemetry and behavioural signals during the session and sends them at configured checkpoints (e.g. onboarding form submit, login, transaction).</p>
                  <p>A limited API-based ingestion path exists for server-side device/IP attributes, but full device fingerprinting and behavioural biometrics require OneSDK.</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm text-[var(--text-secondary)]">
              {fraudRequirementsField?.formLabel || 'Fraud Requirements'}
            </label>
            {fraudRequirementsField?.tooltip && (
              <Tooltip content={fraudRequirementsField.tooltip}>
                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
              </Tooltip>
            )}
          </div>
          <textarea
            className="w-full min-h-28 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
            placeholder={fraudRequirementsField?.placeholder || 'Capture fraud requirements...'}
            value={rulesConfig.fraudRequirements}
            onChange={(e) => updateRulesConfig('fraudRequirements', e.target.value)}
            disabled={disabled}
          />
        </div>

      </div>

      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {riskSection?.formTitle || riskGroup?.formTitle || 'Risk Configuration'}
        </h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <label className="block text-sm font-medium text-[var(--text-primary)]">Risk Configuration Notes</label>
        <p className="text-xs text-[var(--text-muted)]">
          Describe the risk configuration requirements including risk rating system, risk levels, scoring matrix, and any custom risk factors.
        </p>
        <textarea
          className="w-full bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
          rows={6}
          value={riskConfig.notes || ''}
          onChange={(e) => updateRiskConfig('notes', e.target.value)}
          disabled={disabled}
          placeholder="e.g. Customer requires enhanced due diligence for high-risk jurisdictions, FrankieOne risk rating system, custom risk factors for country and industry..."
        />

        {/* Custom Risk Configuration Sheet */}
        <div className="pt-2">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Custom Risk Configuration Sheet</label>
          <p className="text-xs text-[var(--text-muted)] mb-3">
            Create a copy of the risk configuration template for this customer to define risk levels, scoring matrix, and custom risk factors.
          </p>

          {riskConfig.customRiskSheetUrl ? (
            <div className="flex items-center gap-2">
              <a
                href={riskConfig.customRiskSheetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-primary/50 transition-colors group"
              >
                <FileSpreadsheet size={16} className="text-green-400 shrink-0" />
                <span className="text-sm text-[var(--text-primary)] truncate flex-1">
                  {riskConfig.customRiskSheetUrl}
                </span>
                <ExternalLink size={14} className="text-[var(--text-muted)] group-hover:text-primary shrink-0" />
              </a>
              {!disabled && (
                <button
                  type="button"
                  className="p-2 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                  onClick={() => updateRiskConfig('customRiskSheetUrl', '')}
                  title="Remove sheet link"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-500 transition-colors disabled:opacity-50"
                  onClick={() => {
                    const dealName = scope.hubspotDealName || 'Customer'
                    const title = encodeURIComponent(`${dealName} - Risk Configuration`)
                    window.open(
                      `https://docs.google.com/spreadsheets/d/1AZCmFy44Tb73ZYEwOM67EZauNauNqF1eAmaKmh_n5U8/copy?title=${title}`,
                      '_blank'
                    )
                  }}
                  disabled={disabled}
                >
                  <FileSpreadsheet size={16} />
                  Create Risk Sheet from Template
                </button>
              </div>
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">
                  Paste the new sheet URL after creating it
                </label>
                <input
                  type="url"
                  className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={riskConfig.customRiskSheetUrl || ''}
                  onChange={(e) => updateRiskConfig('customRiskSheetUrl', e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
